import { defineStore } from 'pinia'
import { normalize, schema } from 'normalizr'
import { updateStoreState, deleteStoreState, sortObjects } from '../helpers/stateUtils'
import { usePlaceStore, useAlertStore } from './'
import {
  BEAM_API,
} from '../constants'
import moment from 'moment'
import Vue from 'vue'
import i18n from '../plugins/i18n'

const bookingRule = new schema.Entity('booking_rules')

export const useBookingRuleStore = defineStore({
  id: 'booking_rule',
  state: () => ({
    rules: [],
  }),
  getters: {
    findById: (state) => (id) => state.rules.find(find => find.id === id),
    findByIds: (state) => (ids) => state.rules.filter(find => ids.includes(find.id)),
    findByIdsCurrent: (state) => (ids) => {
      const found = state.rules.filter(find => {
        let current = false

        if (find.seasonalBookingRuleDates !== undefined) {
          find.seasonalBookingRuleDates.forEach(date => {
            if (moment(date.dates.end).diff(moment()) >= 0) {
              current = true
            }
          })
        }

        return ids.includes(find.id) && current
      })

      return sortObjects(found, 'label')
    },
    findByIdsHistoric: (state) => (ids) => {
      const found = state.rules.filter(find => {
        let historic = true

        if (find.seasonalBookingRuleDates !== undefined) {
          find.seasonalBookingRuleDates.forEach(date => {
            if (moment(date.dates.end).diff(moment()) >= 0) {
              historic = false
            }
          })
        }

        return ids.includes(find.id) && historic
      })

      return sortObjects(found, 'label')
    },
    isFinalised: (state) => (id) => {
      const bookingRule = state.rules.find(find => find.id === id)
      if (bookingRule) {
        return bookingRule.active === true
      } else {
        return false
      }
    },
    canFinaliseBookingRule: (state) => (id) => {
      let canFinalise = true
      const bookingRule = state.rules.find(find => find.id === id)

      if (!bookingRule) {
        return false
      }

      // check there is at least one check-in and check-out day set
      const hasCheckInDay = bookingRule.stayLengths.filter((dow) => dow[0] > 0 && dow[2] > 0).length > 0

      if (!hasCheckInDay) {
        canFinalise = false
      }

      return canFinalise
    },
  },
  actions: {
    httpCreateRollingBookingRule ({ rollingBookingRule, place }) {
      const placeStore = usePlaceStore()
      const alertStore = useAlertStore()

      return Vue.prototype.$httpEZ.post(`${BEAM_API}/rolling_booking_rules`, rollingBookingRule)
        .then((response) => {
          const { entities } = normalize(response.data, bookingRule)

          updateStoreState(this.rules, Object.values(entities.booking_rules))

          // update place rolling booking rule relation
          place.rollingBookingRule = response.data.id
          updateStoreState(placeStore.places, place)

          alertStore.setAlertSuccess({ message: i18n.t('page-rules.base-rule.actions.add-rule.toast-success') })

          return response.data.id
        })
        .catch((err) => {
          alertStore.setAlertError({ message: i18n.t('page-rules.base-rule.actions.add-rule.toast-error') })

          throw err
        })
    },
    httpUpdateRollingBookingRule ({ rollingBookingRule }) {
      const alertStore = useAlertStore()

      return Vue.prototype.$httpEZ.put(`${BEAM_API}/rolling_booking_rules/${rollingBookingRule.id}`, rollingBookingRule)
        .then((response) => {
          const { entities } = normalize(response.data, bookingRule)
          updateStoreState(this.rules, Object.values(entities.booking_rules))

          alertStore.setAlertSuccess({ message: i18n.t('page-rules.base-rule.actions.update-rule.toast-success') })
        })
        .catch((err) => {
          alertStore.setAlertError({ message: i18n.t('page-rules.base-rule.actions.update-rule.toast-error') })

          throw err
        })
    },
    httpCreateSeasonalBookingRule ({ seasonalBookingRule, place }) {
      const placeStore = usePlaceStore()
      const alertStore = useAlertStore()

      return Vue.prototype.$httpEZ.post(`${BEAM_API}/seasonal_booking_rules`, seasonalBookingRule)
        .then((response) => {
          const { entities } = normalize(response.data, bookingRule)

          updateStoreState(this.rules, Object.values(entities.booking_rules))

          place.seasonalBookingRules.push(response.data.id)

          updateStoreState(placeStore.places, place)

          alertStore.setAlertSuccess({ message: i18n.t('page-rules.seasonal-rule.actions.add-rule.toast-success') })

          return response.data.id
        })
        .catch((err) => {
          alertStore.setAlertError({ message: i18n.t('page-rules.seasonal-rule.actions.add-rule.toast-error') })

          throw err
        })
    },
    httpUpdateSeasonalBookingRule ({ seasonalBookingRule }) {
      const alertStore = useAlertStore()

      return Vue.prototype.$httpEZ.put(`${BEAM_API}/seasonal_booking_rules/${seasonalBookingRule.id}`, seasonalBookingRule)
        .then((response) => {
          const { entities } = normalize(response.data, bookingRule)
          updateStoreState(this.rules, Object.values(entities.booking_rules))

          alertStore.setAlertSuccess({ message: i18n.t('page-rules.seasonal-rule.actions.update-rule.toast-success') })
        })
        .catch(() => {
          alertStore.setAlertError({ message: i18n.t('page-rules.seasonal-rule.actions.update-rule.toast-error') })
        })
    },
    httpDeleteSeasonalBookingRule ({ seasonalBookingRule, place }) {
      const alertStore = useAlertStore()
      const placeStore = usePlaceStore()

      return Vue.prototype.$httpEZ.delete(`${BEAM_API}/seasonal_booking_rules/${seasonalBookingRule.id}`)
        .then(() => {
          deleteStoreState(this.rules, seasonalBookingRule.id)

          // remove the relation from the unit
          place.seasonalBookingRules = place.seasonalBookingRules.filter(item => item !== seasonalBookingRule.id)

          updateStoreState(placeStore.places, place)
          alertStore.setAlertSuccess({ message: i18n.t('page-rules.seasonal-rule.actions.delete-rule.toast-success') })
        })
        .catch(() => {
          alertStore.setAlertError({ message: i18n.t('page-rules.seasonal-rule.actions.delete-rule.toast-error') })
        })
    },
  },
})
